import React from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import theme from '../../styles/theme';

const PaginacaoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
`;

const PaginacaoInfo = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
`;

const PaginacaoControles = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const PaginacaoButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${props => props.$active ? theme.colors.primary[500] : 'transparent'};
  border: 1px solid ${props => props.$active ? theme.colors.primary[500] : theme.colors.border.primary};
  color: ${props => props.$active ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? theme.colors.primary[600] : theme.colors.background.primary};
    border-color: ${theme.colors.primary[500]};
    color: ${props => props.$active ? theme.colors.text.primary : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ItemsPerPageLabel = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.sm};
`;

const ItemsPerPageSelect = styled.select`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

const ParametrosIAPaginacao = ({
  pagination,
  onPageChange,
  onItemsPerPageChange,
  loading
}) => {
  const { currentPage, totalPages, totalItems, itemsPerPage } = pagination;

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginacaoButton
          key={i}
          $active={i === currentPage}
          onClick={() => onPageChange(i)}
          disabled={loading}
        >
          {i}
        </PaginacaoButton>
      );
    }

    return pages;
  };

  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <PaginacaoContainer>
      <PaginacaoInfo>
        Mostrando {startItem} - {endItem} de {totalItems} registros
      </PaginacaoInfo>

      <PaginacaoControles>
        <PaginacaoButton
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || loading}
          title="Primeira página"
        >
          <FiChevronsLeft />
        </PaginacaoButton>
        
        <PaginacaoButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
          title="Página anterior"
        >
          <FiChevronLeft />
        </PaginacaoButton>

        {renderPageNumbers()}

        <PaginacaoButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
          title="Próxima página"
        >
          <FiChevronRight />
        </PaginacaoButton>

        <PaginacaoButton
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || loading}
          title="Última página"
        >
          <FiChevronsRight />
        </PaginacaoButton>
      </PaginacaoControles>

      <ItemsPerPageContainer>
        <ItemsPerPageLabel>Itens por página:</ItemsPerPageLabel>
        <ItemsPerPageSelect
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(parseInt(e.target.value))}
          disabled={loading}
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </ItemsPerPageSelect>
      </ItemsPerPageContainer>
    </PaginacaoContainer>
  );
};

export default ParametrosIAPaginacao;
